// A20 — Reklama (Advertising) module-local data layer. Self-contained: does NOT
// touch `lib/adapters.ts` / `i18n/*` (C-session HOT files) to avoid collisions.
// Backend `/admin/ads/*` is greenfield → every fetcher fail-soft returns an
// empty result (screens show their empty state) until the ads endpoints ship.
// Registration (routes/screenStatus/i18n/nav) = see ./REGISTER.md (C applies).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

/** Local search + client pagination (mirrors adapters.ts private helpers). */
function toResult<T>(
  rows: T[],
  params: FetchParams,
  search?: (r: T) => string,
): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

/** GET a `{ <key>: T[] }` list; dormant endpoints (404/err) yield `[]`. */
async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

export interface AdvertiserRow {
  id: number
  name: string
  status: 'active' | 'suspended' | 'pending'
  balance: number
  spent: number
  campaigns: number
  created_at: string
}

export interface CampaignRow {
  id: number
  advertiser: string
  title: string
  status: 'draft' | 'review' | 'active' | 'paused' | 'rejected' | 'finished'
  budget: number
  spent: number
  cpm: number
  impressions: number
  clicks: number
}

export interface CreativeRow {
  id: number
  campaign: string
  advertiser: string
  kind: 'text' | 'image' | 'video'
  status: 'pending' | 'approved' | 'rejected'
  submitted_at: string
}

export function fetchAdvertisers(params: FetchParams): Promise<FetchResult<AdvertiserRow>> {
  return safeList<AdvertiserRow>('/admin/ads/advertisers', 'advertisers', params.signal).then(
    (rows) => toResult(rows, params, (r) => r.name),
  )
}

/** POST /admin/ads/advertisers/:id/status — active|suspended (A20.03). */
export function setAdvertiserStatus(id: number, status: string): Promise<{ status: string }> {
  return api.post<{ status: string }>(`/admin/ads/advertisers/${id}/status`, { status })
}

export function fetchCampaigns(params: FetchParams): Promise<FetchResult<CampaignRow>> {
  return safeList<CampaignRow>('/admin/ads/campaigns', 'campaigns', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.advertiser}`),
  )
}

export function fetchCreatives(params: FetchParams): Promise<FetchResult<CreativeRow>> {
  return safeList<CreativeRow>('/admin/ads/creatives', 'creatives', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.campaign} ${r.advertiser}`),
  )
}

/** A20.10 — campaigns awaiting review (`?status=review`, dormant → []). */
export function fetchReviewCampaigns(params: FetchParams): Promise<FetchResult<CampaignRow>> {
  return safeList<CampaignRow>(
    '/admin/ads/campaigns?status=review',
    'campaigns',
    params.signal,
  ).then((rows) => toResult(rows, params, (r) => `${r.title} ${r.advertiser}`))
}

/** POST /admin/ads/campaigns/:id/status — approve→'active' / reject→'rejected'
 *  (backend `SetCampaignStatus`, A20.10/A20.11). */
export function setCampaignStatus(id: number, status: string): Promise<{ status: string }> {
  return api.post<{ status: string }>(`/admin/ads/campaigns/${id}/status`, { status })
}

/** POST /admin/ads/creatives/:id/moderate — approve/reject a creative (A20.05). */
export function moderateCreative(
  id: number,
  decision: 'approve' | 'reject',
  reason?: string,
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/ads/creatives/${id}/moderate`, { decision, reason })
}

/**
 * Module-local RU strings — kept here (not `i18n/*`, which is C-session HOT) so
 * the module compiles standalone. REGISTER.md lists the keys to fold into i18n.
 */
export const ADS_STR = {
  advertisers: 'Рекламодатели',
  campaigns: 'Кампании',
  creatives: 'Модерация креативов',
  advertiser: 'Рекламодатель',
  title: 'Кампания',
  status: 'Статус',
  balance: 'Баланс',
  spent: 'Потрачено',
  budget: 'Бюджет',
  cpm: 'CPM',
  impressions: 'Показы',
  clicks: 'Клики',
  created: 'Создан',
  submitted: 'Отправлен',
  kind: 'Тип',
  approve: 'Одобрить',
  reject: 'Отклонить',
  approved: 'Креатив одобрен',
  rejected: 'Креатив отклонён',
  moderateFailed: 'Не удалось выполнить',
  // A20.10 — campaign approval
  approval: 'Одобрение кампаний',
  campaignApproved: 'Кампания одобрена',
  rejectTitle: (name: string) => `Отклонить кампанию «${name}»?`,
  rejectDesc: 'Рекламодатель получит уведомление о решении.',
  requestCreated: 'Запрос создан',
  // A20.03 — advertiser status
  suspend: 'Заблокировать',
  activate: 'Разблокировать',
  statusChanged: 'Статус обновлён',
} as const
